/**
 * photo_share_controller.js — the "Show to client" tick on one gallery photo.
 *
 * A gallery photo is a working photo for the team until this is ticked; a
 * ticked photo appears in the Photos section of the client's PDF and public
 * page (application/services/client_photo_service.py decides that, from
 * Attachment.client_visible).
 *
 * The same toggle renders in two places for one photo: on its strip tile and
 * in the lightbox (photos past the fourth are reachable only there). Each is
 * its own controller instance keyed by data-photo-share-id-value, so after a
 * save every copy for that id is repainted, not just the one tapped.
 *
 * Markup: the share_toggle macro in templates/components/detail/entity_photos.html
 * (cloned from its <template> by gallery_controller.js for new tiles and the
 * lightbox). Backing API: PATCH /api/v1/attachments/<id> {client_visible}.
 */

import { Controller } from "@hotwired/stimulus";

function csrfHeaders() {
    var token = (typeof FormValidator !== 'undefined' && FormValidator.getCsrfToken)
        ? FormValidator.getCsrfToken() : null;
    return token ? { 'X-CSRFToken': token } : {};
}

function notify(message, level) {
    if (typeof window.showNotification === 'function') {
        window.showNotification(message, level);
    }
}

/** Paint every toggle for one photo with the saved state. */
function paintAll(attachmentId, shared) {
    var selector = '[data-photo-share-id-value="' + attachmentId + '"]';
    document.querySelectorAll(selector).forEach(function (el) {
        el.classList.toggle('is-on', shared);
        el.setAttribute('aria-pressed', shared ? 'true' : 'false');
    });
}

export default class extends Controller {
    static values = { id: String };

    // The tile is a lightbox trigger and a drag handle; the tick must do
    // neither, so the click stops here before gallery_controller's document
    // listener sees it.
    async toggle(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.idValue || this._saving) return;
        var shared = this.element.getAttribute('aria-pressed') !== 'true';
        this._saving = true;
        try {
            var resp = await fetch('/api/v1/attachments/' + this.idValue, {
                method: 'PATCH',
                headers: Object.assign({ 'Content-Type': 'application/json' }, csrfHeaders()),
                body: JSON.stringify({ client_visible: shared }),
                credentials: 'same-origin'
            });
            if (!resp.ok) throw new Error('Could not update the photo');
            paintAll(this.idValue, (await resp.json()).client_visible);
        } catch (err) {
            notify(err.message, 'error');
        } finally {
            this._saving = false;
        }
    }
}
